import React, { useEffect, useState } from 'react';
import { Button, Flex, FlexProps, Text } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import arrowIcon from '../../Resources/Images/Icons/arrow_icon.svg';
import { PlanType } from '../../Utils/backStageApi';
import { numAddComma } from '../../Utils/common';
import { GenericTable } from '../Common/GenericTable';
import { TablePager } from '../Common/TablePager';

export interface PlanItem extends PlanType {}

export type PlanSortField = 'annual_limit' | 'issuer_count' | null;

interface Props extends Pick<FlexProps, 'mt'> {
  plans: PlanItem[];
  sortField: PlanSortField;
  sortDirection: 'asc' | 'desc';
  onSortToggle: (field: Exclude<PlanSortField, null>) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20] as const;

const TABLE_CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

export const PlanTable: React.FC<Props> = ({ plans, sortField, sortDirection, onSortToggle, mt }) => {
  const history = useHistory();
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const totalItems = plans.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const paginated = plans.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [plans]);

  const columns = [
    { label: '方案代碼', width: '220px', paddingLeft: '40px' },
    { label: '中文名稱', width: '220px' },
    {
      label: '年發證上限',
      width: '250px',
      sortDirection: sortField === 'annual_limit' ? sortDirection : undefined,
      onSort: () => onSortToggle('annual_limit'),
      sortArrowSrc: sortField === 'annual_limit' ? arrowIcon : undefined,
      sortArrowAlt: '排序',
    },
    {
      label: '使用機構數',
      width: '250px',
      sortDirection: sortField === 'issuer_count' ? sortDirection : undefined,
      onSort: () => onSortToggle('issuer_count'),
      sortArrowSrc: sortField === 'issuer_count' ? arrowIcon : undefined,
      sortArrowAlt: '排序',
    },
    { label: '', width: '100px' },
  ];

  const renderRow = (item: PlanItem) => (
    <Flex key={item.id} w="1040px" minH="60px" align="stretch">
      <Flex w="220px" minW="220px" align="center" pl="40px" pr="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.code} fontFamily="monospace" fontSize="15px">
          {item.code}
        </Text>
      </Flex>

      <Flex w="220px" minW="220px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.name_zh}>
          {item.name_zh}
        </Text>
      </Flex>

      <Flex w="250px" minW="250px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{item.annual_limit === null ? '無上限' : numAddComma(item.annual_limit)}</Text>
      </Flex>

      <Flex w="250px" minW="250px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{numAddComma(item.issuer_count)}</Text>
      </Flex>

      <Flex w="100px" minW="100px" justify="center" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Button
          variant="unstyled"
          minW="37px"
          h="22px"
          onClick={() => history.push('/bs/plan/edit', { plan: item })}
        >
          <Text
            fontFamily="'Open Sans', sans-serif"
            fontSize="18px"
            fontWeight={600}
            lineHeight="120%"
            letterSpacing="0.01em"
            textDecoration="underline"
            color="#006699"
          >
            編輯
          </Text>
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" w="1040px" mt={mt}>
      <GenericTable columns={columns} isEmpty={paginated.length === 0} emptyText="目前沒有方案資料">
        {paginated.map(renderRow)}
      </GenericTable>

      <TablePager
        mt="50px"
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={safePage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        labels={{
          show: '顯示',
          itemsUnit: '筆',
          range: (s, e) => `${s} - ${e} 筆`,
          prevPage: '上一頁',
          nextPage: '下一頁',
        }}
      />
    </Flex>
  );
};

export default PlanTable;
